/**
 * Публичный путь, который раздаёт nginx. Он намеренно НЕ на шлюзе: config-сервис умирает
 * вместе со всеми остальными на 10-минутном выкате, а nginx в этот момент продолжает отдавать
 * закешированную копию (`proxy_cache_use_stale`). Проверка версии обязана переживать выкат —
 * иначе она бесполезна ровно тогда, когда нужна.
 */
export const APP_STATUS_PATH = '/app/status.json';

/**
 * Проверка не должна удлинять холодный старт. Три секунды — потолок ожидания; всё, что дольше,
 * трактуется как «не знаю» и не блокирует ничего (fail-open).
 */
export const APP_STATUS_TIMEOUT_MS = 3_000;

/** Последний удачно прочитанный ответ. Применяется, пока не приехал новый. */
export const APP_STATUS_CACHE_KEY = 'app-status:last-good';

/**
 * Отметка «баннер закрыт» привязана к ЦЕЛЕВОЙ версии, а не к текущей: иначе после обновления
 * ключ сменится вместе с build и баннер вернётся к человеку, который только что всё сделал.
 */
export const bannerDismissKey = (target: number) => `app-update:dismissed:v${target}`;

/**
 * Через сколько закрытый баннер вернуть. Прямой рекомендации у Google нет; в ближайшем аналоге
 * (In-App Review API) он держит квоту порядка «раз в месяц» и запрещает кнопку-триггер. Трое
 * суток — компромисс: напоминание остаётся заметным, но не превращается в назойливость.
 */
export const BANNER_DISMISS_MS = 3 * 24 * 60 * 60 * 1_000;

/**
 * Не дёргать сеть на каждое возвращение из фона. Приложение живёт открытым сутками, и
 * `appStateChange` прилетает при каждом переключении между приложениями.
 */
export const RECHECK_MIN_INTERVAL_MS = 30 * 60 * 1_000;

/**
 * Сколько живёт сохранённая копия ответа.
 *
 * Копия нужна, чтобы проверка версии пережила выкат: пока nginx перезапускается, приложение
 * пользуется последним известным значением. Но вечной она быть не может — иначе однажды
 * поднятый порог блокировки невозможно снять у человека без сети, ведь снимает его только
 * удачный запрос. Сутки — заметно больше любого выката и заметно меньше, чем срок, за который
 * человек успеет решить, что приложение сломалось навсегда.
 */
export const APP_STATUS_CACHE_TTL_MS = 24 * 60 * 60 * 1_000;

/**
 * Как часто экран работ перепроверяет состояние.
 *
 * Полминуты: выкат длится минуты, и человек, сидящий перед заглушкой, должен увидеть
 * возвращение продукта сам, не перезапуская приложение. Чаще незачем — ответ всё равно лежит
 * в кеше nginx, а таймер работает только пока экран на виду.
 */
export const MAINTENANCE_RECHECK_MS = 30_000;

/**
 * Как часто ответ шлюза «сервер закрыт» имеет право отправить нас за статусом.
 *
 * 503 приходит на каждый запрос, а на живом экране их десятки: лента, счётчики, чат. Без
 * порога первый же выкат обернулся бы очередью одинаковых проверок. Десять секунд — вдвое
 * меньше, чем живёт ответ в кеше nginx, так что задержки это не добавляет.
 */
export const SERVER_CLOSED_RECHECK_MS = 10_000;

/**
 * Как часто спрашивать Play, скачалось ли flexible-обновление.
 *
 * Прогресс загрузки Play наружу не отдаёт — только признак «скачано», поэтому иначе никак.
 * Три секунды: вызов локальный (никакой сети, обращение к сервису Play на самом устройстве),
 * а человек, согласившийся обновиться, ждёт кнопку «Перезапустить» и не должен смотреть на
 * «Загружается…» дольше, чем оно есть на самом деле. Таймер работает только во время загрузки.
 */
export const FLEXIBLE_POLL_MS = 3_000;

/**
 * Сколько всего ждём скачивания, прежде чем вернуть баннеру кнопку «Обновить».
 *
 * 🔴 Из состояния загрузки не было выхода вовсе: ни таймера, ни счётчика попыток. Play наружу
 * отдаёт только признак «скачано», а провал загрузки - отменённую, упавшую, оставшуюся без
 * места - не отдаёт никак; RESULT_IN_APP_UPDATE_FAILED пишется в лог оболочки и наружу идёт тем
 * же «человек отказался». Значит сорвавшаяся загрузка оставляла «Загружается…» навсегда: «Позже»
 * гасило баннер, но не состояние, и при следующем показе он снова «загружался».
 *
 * Пять минут: flexible-обновление приложения - это десятки мегабайт, на медленной мобильной сети
 * минута-две штатны, и обрывать живую загрузку нельзя. При этом скачанное уже не потеряется -
 * признак «скачано» проверяется первым и переживает перезапуск, - так что цена ошибки в эту
 * сторону всего одна: лишнее нажатие «Обновить», которое подберёт готовое.
 */
export const FLEXIBLE_TIMEOUT_MS = 5 * 60_000;
